import { useToast, LoadingPage } from "@qubit/autoparts";
import * as Sentry from "@sentry/react";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { setSignalREnabled } from "~/app/app.slice";
import { useAppSelector, useAppDispatch } from "~/app/store";
import { selectHandRaisedForWorkstation } from "~/features/andon/andon.selectors";
import { clearEventData } from "~/features/andon/andon.slice";
import { useCloseWorkstationWithErrorToast } from "~/hooks/useCloseWorkstationWithErrorToast";
import { mixpanelLogout } from "~/lib/mixpanel-tracking";
import { sendUserEventAction } from "~/redux/actions/userEvents";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useToggleHandRaiseMutation } from "~/redux/warehouse/andon.hooks";

import HandRaiseReminderModal from "./HandRaiseReminderModal";
import {
  clearLoginState,
  qubitAuthClient,
  andonAuthClient,
  setUnauthorizedLogoutRequested
} from "./login.slice";
import { AuthMethod } from "./login.slice.interfaces";

export const Logout = () => {
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const navigate = useNavigate();

  const closeWorkstationWithErrorToast = useCloseWorkstationWithErrorToast();
  const [toggleHandRaise] = useToggleHandRaiseMutation();

  const [showHandPrompt, setShowHandPrompt] = useState(false);
  const [continueLogout, setContinueLogout] = useState(false);

  const loginPath =
    useAppSelector((state) => state.app.siteLoginPath) || "/login";
  const customLoginPath = useAppSelector((state) => state.site.customLoginPath);
  const authMethod = useAppSelector((state) => state.login.authMethod);
  const isUserLoggedIn = useAppSelector((state) => state.login.isUserLoggedIn);
  const siteWorkstationId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.id
  );
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const selectedAutostoreGridId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.autostoreGridId
  );
  const handRaised = useAppSelector((state) => {
    return siteWorkstationId
      ? selectHandRaisedForWorkstation(siteWorkstationId)(state)
      : { raised: false, timestamp: null };
  });
  const unauthorizedLogoutRequested = useAppSelector(
    (state) => state.login.unauthorizedLogoutRequested
  );

  // used to preserve useEffect dependencies while ensuring only one logout action is taken
  // this is because serveral code paths can trigger the redirect to logout
  const hasLoggedOut = useRef(false);

  const performAuthLogout = async (
    authMethod: AuthMethod | null,
    customLoginPath: string | undefined,
    loginPath: string
  ): Promise<void> => {
    let authClient = null;

    switch (authMethod) {
      case "QUBIT":
      case "E2E":
        authClient = qubitAuthClient;
        break;
      case "ANDON":
        authClient = andonAuthClient;
        break;
      case "BEV_OPERATOR":
      default:
        authClient = null;
        break;
    }

    if (authClient) {
      const logoutParams = customLoginPath
        ? { returnTo: customLoginPath }
        : {
            federated: true,
            returnTo: `${window.location.origin}${loginPath}`
          };

      try {
        await authClient.logout({
          logoutParams
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: "auth-logout" },
          extra: { authMethod }
        });
      }
    }
  };

  useEffect(() => {
    const handleLogout = async () => {
      if (hasLoggedOut.current) {
        return;
      }
      hasLoggedOut.current = true;
      dispatch(setUnauthorizedLogoutRequested(false));

      // Clear event data and workstation
      dispatch(clearEventData());

      if (usersFulfillmentCenter) {
        await closeWorkstationWithErrorToast();
      }

      // Send user event action
      try {
        await dispatch(sendUserEventAction("UserLoggedOut"));
      } catch {
        errorToast("Failed to send user event action");
      }

      // Logout of Mixpanel (error handled in function)
      mixpanelLogout();

      // Clear login state
      dispatch(clearLoginState());

      dispatch(setSignalREnabled(false));

      if (authMethod && ["BEV_OPERATOR", "BEV_QUBIT"].includes(authMethod)) {
        navigate(loginPath);
      } else {
        await performAuthLogout(authMethod, customLoginPath, loginPath);
      }
    };

    if (isUserLoggedIn) {
      if (
        !unauthorizedLogoutRequested &&
        handRaised.raised &&
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        !continueLogout
      ) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
        setShowHandPrompt(true);
        return;
      }
      void handleLogout();
    } else {
      navigate(loginPath);
    }
  }, [
    dispatch,
    closeWorkstationWithErrorToast,
    usersFulfillmentCenter,
    errorToast,
    customLoginPath,
    loginPath,
    navigate,
    authMethod,
    isUserLoggedIn,
    handRaised,
    continueLogout,
    setShowHandPrompt,
    unauthorizedLogoutRequested
  ]);

  return (
    <>
      <LoadingPage loadingMessage={t("logging off")} />
      {showHandPrompt && (
        <HandRaiseReminderModal
          variant="logout"
          onLowerHand={async () => {
            if (selectedAutostoreGridId && siteWorkstationId) {
              try {
                await toggleHandRaise({
                  autostoreGridId: selectedAutostoreGridId,
                  workstationId: siteWorkstationId,
                  raiseHand: false
                });
              } catch {
                errorToast("Failed to lower hand before logout");
              }
            }
            setContinueLogout(true);
            setShowHandPrompt(false);
          }}
          onKeepHandRaised={() => {
            setContinueLogout(true);
            setShowHandPrompt(false);
          }}
        />
      )}
    </>
  );
};
